import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const EMPTY = { name: "", photo: null, review: "", rating: 5, is_active: true };

export default function AdminTestimonials() {
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(null); // null | EMPTY | testimonial
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = () => api.get("/admin/testimonials").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setModal({ ...modal, photo: res.data.path });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { name: modal.name, photo: modal.photo, review: modal.review, rating: Number(modal.rating), is_active: modal.is_active };
      if (modal.id) {
        await api.put(`/admin/testimonials/${modal.id}`, payload);
      } else {
        await api.post("/admin/testimonials", payload);
      }
      toast.success("Témoignage enregistré");
      setModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-10">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl">Témoignages</h1>
          <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mt-1">{list.length} avis</p>
        </div>
        <button className="btn-primary" onClick={() => setModal({ ...EMPTY })} data-testid="new-testimonial-btn">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <p className="text-[#6B6B6B] col-span-full text-center py-12">Aucun témoignage.</p>}
        {list.map((t) => (
          <div key={t.id} className="border border-black/5 bg-white overflow-hidden" data-testid={`testimonial-card-${t.id}`}>
            {t.photo && (
              <div className="aspect-video w-full overflow-hidden bg-[#FAF7F0] border-b border-[#B8860B]/20">
                <img src={fileUrl(t.photo)} className="w-full h-full object-cover" alt={t.name} />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-lg">{t.name}</div>
                <div className="text-[#B8860B]">{"★".repeat(t.rating)}</div>
              </div>
              {!t.is_active && <span className="text-xs text-red-400 uppercase tracking-widest">Caché</span>}
              <p className="font-citation italic text-sm text-[#1A1A1A]/80">{`"${t.review}"`}</p>
              <div className="mt-4 flex gap-2 text-xs">
                <button onClick={() => setModal(t)} className="text-[#B8860B] uppercase tracking-widest" data-testid={`edit-testimonial-${t.id}`}>Modifier</button>
                <button onClick={() => remove(t.id)} className="text-red-400 uppercase tracking-widest ml-auto" data-testid={`delete-testimonial-${t.id}`}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6 overflow-y-auto py-10" onClick={() => setModal(null)}>
          <div className="bg-white border border-[#B8860B]/30 max-w-md w-full p-8 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="testimonial-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl text-[#B8860B]">{modal.id ? "Modifier" : "Nouveau"} témoignage</h3>
              <button onClick={() => setModal(null)}><X /></button>
            </div>
            <div>
              <label className="label-tiny block mb-1">Nom</label>
              <input className="input-gold" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="testimonial-name" />
            </div>
            <div>
              <label className="label-tiny block mb-1">Photo (paysage / landscape recommandée)</label>
              <div className="flex items-start gap-3">
                {modal.photo && (
                  <div className="w-32 aspect-video bg-[#FAF7F0] border border-[#B8860B]/30 overflow-hidden flex-shrink-0">
                    <img src={fileUrl(modal.photo)} className="w-full h-full object-cover" alt="" />
                  </div>
                )}
                <input type="file" ref={fileInput} accept="image/*" onChange={uploadPhoto} className="text-xs" />
              </div>
            </div>
            <div>
              <label className="label-tiny block mb-1">Avis</label>
              <textarea className="input-gold min-h-[100px]" value={modal.review} onChange={(e) => setModal({ ...modal, review: e.target.value })} data-testid="testimonial-review" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-tiny block mb-1">Note</label>
                <select className="input-gold" value={modal.rating} onChange={(e) => setModal({ ...modal, rating: e.target.value })}>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
              <label className="flex items-end gap-2 text-sm">
                <input type="checkbox" checked={modal.is_active} onChange={(e) => setModal({ ...modal, is_active: e.target.checked })} />
                Actif
              </label>
            </div>
            <button className="btn-primary w-full" onClick={save} disabled={uploading} data-testid="save-testimonial-btn">
              {uploading ? "Envoi photo..." : "Enregistrer"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
